import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SportBadge } from '@/components/SportBadge';
import { 
  ChevronRight, Users, Swords, Calendar, MapPin, 
  Trophy, UserPlus, Bell
} from 'lucide-react';
import { format, isToday, isTomorrow, differenceInDays } from 'date-fns';
import { vi } from 'date-fns/locale';
import type { Sport } from '@/types';

interface ClubActivity {
  id: string;
  type: 'match' | 'challenge' | 'training';
  clubId: string;
  clubName: string;
  clubAvatar: string | null;
  clubSport: Sport;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  actionUrl: string;
}

interface ClubActivitiesSectionProps {
  limit?: number;
}

export function ClubActivitiesSection({ limit = 3 }: ClubActivitiesSectionProps) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  
  const { data: clubActivities = [], isLoading } = useQuery({
    queryKey: ['club-activities', profile?.id],
    queryFn: async (): Promise<ClubActivity[]> => {
      if (!profile?.id) return [];
      
      // Get user's clubs
      const { data: memberships } = await supabase
        .from('club_members')
        .select('club_id')
        .eq('profile_id', profile.id);
      
      if (!memberships || memberships.length === 0) return [];
      
      const clubIds = memberships.map(m => m.club_id);
      
      // Get upcoming club matches
      const { data: clubMatches } = await supabase
        .from('matches')
        .select(`
          id, date, time, location, sport,
          home_club:clubs!matches_home_club_id_fkey(id, name, avatar, sport),
          away_club:clubs!matches_away_club_id_fkey(id, name, avatar)
        `)
        .eq('is_club_match', true)
        .in('home_club_id', clubIds)
        .eq('status', 'upcoming')
        .gte('date', format(new Date(), 'yyyy-MM-dd'))
        .order('date', { ascending: true })
        .order('time', { ascending: true })
        .limit(limit);
      
      // Get pending challenges to user's clubs
      const { data: challenges } = await supabase
        .from('club_match_challenges')
        .select(`
          id, date, time, location, sport,
          challenger:clubs!club_match_challenges_challenger_club_id_fkey(id, name, avatar),
          challenged:clubs!club_match_challenges_challenged_club_id_fkey(id, name, avatar)
        `)
        .in('challenged_club_id', clubIds)
        .eq('status', 'pending')
        .gte('date', format(new Date(), 'yyyy-MM-dd'))
        .order('date', { ascending: true })
        .limit(limit);
      
      const activities: ClubActivity[] = [];
      
      // Add matches
      (clubMatches || []).forEach((match: any) => {
        const userClub = clubIds.includes(match.home_club?.id) ? match.home_club : match.away_club;
        const opponentClub = clubIds.includes(match.home_club?.id) ? match.away_club : match.home_club;
        
        activities.push({
          id: match.id,
          type: 'match',
          clubId: userClub?.id,
          clubName: userClub?.name ?? 'CLB',
          clubAvatar: userClub?.avatar,
          clubSport: match.sport as Sport,
          title: `Trận đấu vs ${opponentClub?.name ?? 'CLB đối thủ'}`,
          description: 'Trận đấu CLB sắp diễn ra',
          date: match.date,
          time: match.time,
          location: match.location,
          actionUrl: `/matches/${match.id}`,
        });
      });
      
      // Add challenges
      (challenges || []).forEach((challenge: any) => {
        activities.push({
          id: challenge.id,
          type: 'challenge',
          clubId: challenge.challenged?.id,
          clubName: challenge.challenged?.name ?? 'CLB',
          clubAvatar: challenge.challenged?.avatar,
          clubSport: challenge.sport as Sport,
          title: `Thách đấu từ ${challenge.challenger?.name ?? 'CLB'}`,
          description: 'Đang chờ phản hồi',
          date: challenge.date,
          time: challenge.time,
          location: challenge.location,
          actionUrl: `/clubs/${challenge.challenged?.id}`,
        });
      });
      
      // Sort by date and return limited
      return activities
        .sort((a, b) => {
          const dateA = new Date(`${a.date}T${a.time}`);
          const dateB = new Date(`${b.date}T${b.time}`);
          return dateA.getTime() - dateB.getTime();
        })
        .slice(0, limit);
    },
    enabled: !!profile?.id,
    staleTime: 2 * 60 * 1000,
  });
  
  if (isLoading) {
    return (
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-accent" />
            <h3 className="font-bold text-foreground">Hoạt động CLB</h3>
          </div>
        </div>
        <div className="space-y-2">
          {[1, 2].map(i => (
            <div key={i} className="animate-pulse bg-muted/50 rounded-xl h-24" />
          ))}
        </div>
      </section>
    );
  }
  
  if (clubActivities.length === 0) {
    return null; // Don't show section if no activities
  }
  
  const getDateLabel = (dateStr: string): string => {
    const date = new Date(dateStr);
    if (isToday(date)) return 'Hôm nay';
    if (isTomorrow(date)) return 'Ngày mai';
    const daysUntil = differenceInDays(date, new Date());
    if (daysUntil <= 7) return format(date, 'EEEE', { locale: vi });
    return format(date, 'dd/MM', { locale: vi });
  };
  
  const getActivityIcon = (type: ClubActivity['type']) => {
    switch (type) {
      case 'match': return <Swords className="w-4 h-4" />;
      case 'challenge': return <Trophy className="w-4 h-4" />;
      case 'training': return <Calendar className="w-4 h-4" />;
    }
  };
  
  const getActivityBadgeVariant = (type: ClubActivity['type']) => {
    switch (type) {
      case 'match': return 'default';
      case 'challenge': return 'secondary';
      case 'training': return 'outline';
    }
  };
  
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-accent/20">
            <Users className="w-5 h-5 text-accent" />
          </div>
          <div>
            <h3 className="font-bold text-foreground">Hoạt động CLB</h3>
            <p className="text-xs text-muted-foreground">
              {clubActivities.length} hoạt động sắp tới
            </p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-muted-foreground hover:text-accent h-8 px-2"
          onClick={() => navigate('/clubs')}
        >
          Xem CLB
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="space-y-2">
        {clubActivities.map((activity) => (
          <Card 
            key={activity.id}
            className="cursor-pointer hover:bg-muted/50 transition-colors border-l-4 border-l-accent"
            onClick={() => navigate(activity.actionUrl)}
          >
            <CardContent className="p-3">
              <div className="flex items-start gap-3">
                {/* Club avatar */}
                <Avatar className="h-10 w-10">
                  <AvatarImage src={activity.clubAvatar || undefined} />
                  <AvatarFallback className="bg-accent/20 text-accent">
                    {activity.clubName[0]}
                  </AvatarFallback>
                </Avatar>
                
                {/* Activity details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant={getActivityBadgeVariant(activity.type)} className="text-xs">
                      {getActivityIcon(activity.type)}
                      <span className="ml-1">
                        {activity.type === 'match' ? 'Trận đấu' : 
                         activity.type === 'challenge' ? 'Thách đấu' : 'Tập luyện'}
                      </span>
                    </Badge>
                    <SportBadge sport={activity.clubSport} size="sm" />
                  </div>
                  
                  <h4 className="font-medium text-sm text-foreground truncate mb-1">
                    {activity.title}
                  </h4>
                  
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      <span>{getDateLabel(activity.date)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>{activity.time.slice(0, 5)}</span>
                    </div>
                  </div>
                  
                  {activity.location && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{activity.location}</span>
                    </div>
                  )}
                </div>
                
                {/* Action indicator */}
                {activity.type === 'challenge' && (
                  <Badge variant="destructive" className="text-xs animate-pulse">
                    <Bell className="w-3 h-3 mr-1" />
                    Mới
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
